import { AfterContentChecked, AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, EventEmitter, forwardRef, Input, Output, QueryList, TemplateRef } from '@angular/core';
import { PrimeTemplate } from 'primeng/api';

/*
  PrimeNG's TabView, kept as our own pair of components on top of PrimeNG's Tabs.

  TabView was removed in PrimeNG 20. Tabs replaces it with a different shape - tabs keyed by a
  value, and a panel's content only in the page while its tab is open - which our 19 tabbed pages
  were not written for: they pick tabs by position (activeIndex, onChange's index), position shifts
  with @if and @for panels, and some panels rely on content templates being created lazily.

  So these take exactly the inputs, outputs and templates TabView did and behave as it did:
  - a tab's index is its position among the panels actually rendered
  - a panel's plain content is always in the page, hidden while its tab is not open
  - a panel's content template (pTemplate="content", or any name but "header") is created when its
    tab is first opened, then kept if cache is on (the default) or destroyed when left if it is off
  - a header template replaces the header text and icons

  Which panel is open is settled in the tab view's ngAfterContentChecked, once Angular has
  refreshed its panel list. Panels added by @if or @for are checked before that list is
  refreshed, so a panel whose state changes there is re-rendered on the spot (they are OnPush) -
  otherwise it would briefly show the wrong state, and dev mode would throw ExpressionChanged.
*/

@Component({
  selector: 'app-tabview',
  //A block, as TabView's own element was - pages put layout classes like col-12 on it
  styleUrls: ['./host-block.css'],
  template: `
    <p-tabs [value]="selectedIndex" (valueChange)="select($event)" [scrollable]="true" [showNavigators]="scrollable" [ngStyle]="style" [ngClass]="styleClass">
      <p-tablist>
        @for (panel of panels; track panel; let i = $index) {
          <p-tab [value]="i" [disabled]="panel.disabled" [ngClass]="panel.headerStyleClass">
            @if (panel.headerTemplate) {
              <ng-container *ngTemplateOutlet="panel.headerTemplate"></ng-container>
            } @else {
              @if (panel.leftIcon) {
                <span class="p-tabview-left-icon" [ngClass]="panel.leftIcon"></span>
              }
              {{ panel.header }}
              @if (panel.rightIcon) {
                <span class="p-tabview-right-icon" [ngClass]="panel.rightIcon"></span>
              }
            }
          </p-tab>
        }
      </p-tablist>
      <p-tabpanels>
        <ng-content></ng-content>
      </p-tabpanels>
    </p-tabs>
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TabViewComponent implements AfterContentChecked {
  @Input() style: { [key: string]: any } | null = null;
  @Input() styleClass: string;

  //TabView's tab strip always scrolled sideways when the tabs did not fit (scrollbar hidden);
  //scrollable only added the arrow buttons. Tabs only scrolls when scrollable, so it always is here,
  //and this decides the arrows.
  @Input() scrollable: boolean = false;

  @Input() activeIndex: number;
  @Output() activeIndexChange = new EventEmitter<number>();

  //Same shape as TabView's: { originalEvent, index }
  @Output() onChange = new EventEmitter<{ originalEvent: Event | null, index: number }>();

  @ContentChildren(forwardRef(() => TabPanelComponent)) panels: QueryList<TabPanelComponent>;

  //TabView opened the first tab when activeIndex was not set
  get selectedIndex(): number {
    return this.activeIndex ?? 0;
  }

  ngAfterContentChecked(): void {
    this.panels?.forEach((panel, i) => panel.setSelected(i === this.selectedIndex));
  }

  select(value: any) {
    const index = Number(value);
    if (index === this.selectedIndex)
      return;
    this.activeIndex = index;
    this.activeIndexChange.emit(index);
    this.onChange.emit({ originalEvent: null, index });
  }
}

@Component({
  selector: 'app-tabpanel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./host-block.css'],
  template: `
    <div class="p-tabpanel" role="tabpanel" [hidden]="!selected">
      <ng-content></ng-content>
      @if (showContentTemplate) {
        <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
      }
    </div>
    `,
  standalone: false
})
export class TabPanelComponent implements AfterContentInit {
  @Input() header: string = '';
  @Input() leftIcon: string;
  @Input() rightIcon: string;
  @Input() disabled: boolean = false;
  @Input() cache: boolean = true;
  @Input() headerStyleClass: string;

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate>;

  headerTemplate: TemplateRef<any> | null = null;
  contentTemplate: TemplateRef<any> | null = null;

  selected: boolean = false;
  //Whether the content template has been created, for cache
  private loaded: boolean = false;

  constructor(private cd: ChangeDetectorRef) { }

  ngAfterContentInit(): void {
    //As TabView: "header" is the header, anything else is the content
    this.templates.forEach(t => {
      if (t.getType() == 'header')
        this.headerTemplate = t.template;
      else
        this.contentTemplate = t.template;
    });
  }

  //Called by the tab view once it knows which panel is open
  setSelected(selected: boolean) {
    if (selected === this.selected)
      return;
    this.selected = selected;
    if (selected)
      this.loaded = true;
    this.cd.detectChanges();
  }

  //Created on first opening, then kept (cache) or only while open (no cache)
  get showContentTemplate(): boolean {
    return !!this.contentTemplate && (this.cache ? this.loaded : this.selected);
  }
}
